import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from '../../services/configuration.service';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { timeZones } from '../../../assets/constants/timezones';
import { HostRoute } from '../enums/Host.enum';
import { LocalStorageService } from 'src/app/services/local-storage.service';


@Injectable({
  providedIn: 'root'
})

export class SiteService {
  caretakerUrl = '';
  haystackTimezoneMap: any = {};
  constructor(private http: HttpClient,
    private configService: ConfigurationService,
    private sessionStorage: LocalStorageService,
    ) { 
      this.caretakerUrl = this.configService.getConfig('auth')?.caretakerUrl;
      this.haystackTimezoneMap = timeZones;
    }

  getSites(portal:string): Observable<any> {
    if(portal != HostRoute.FACILISIGHT) {
      const data = `ver:"2.0"\nfilter\n"site"`;
       const regions = this.configService.getRegions();
      const requestRegions: Observable<any>[] = [];
      regions.forEach(region => {
        requestRegions.push(this.http.post(`${this.configService.getConfig(region.regionId).haystackUrl}v1/read`, data).pipe(catchError(() => of([]))) );
      });
      
      return forkJoin(requestRegions).pipe(
        map((responses: any[]) => {
          const mergedRows = responses.flatMap((response: any, index: number) =>
            (response?.rows || []).map((site: any) => ({
              ...site,
              region: regions[index]
            }))
          );
          const sortedRows = regions.length === 1 ? mergedRows : mergedRows.sort((a: any, b: any) => {
            const firstDate = new Date((a?.createdDateTime || '').toString().replace(/^t:/, '')).getTime() || 0;
            const secondDate = new Date((b?.createdDateTime || '').toString().replace(/^t:/, '')).getTime() || 0;
            return firstDate - secondDate;
          });
          return { rows: sortedRows };
        }),
        catchError(this.handleError)
      );
    } else {
      const userId = this.sessionStorage.getUserId();
      return this.http.get(`${this.caretakerUrl}/user/${userId}/sites?portal=facilisight`).pipe(
        map((res:any) => ({
          rows: res.sites.map((site:any) => ({
            id: 'r:'+site.siteId.replace(/@/g, ''),
            dis: site.siteName,
            ...site
          })).reverse()
        }))
      );
    }
  }

  // Method to get the list of sites for the support portal based on the current user's ID.
  getSitesForSupportPortal(): Observable<any[]> {
    const userId = this.sessionStorage.getUserId();
    return this.http.get<any>(`${this.caretakerUrl}/user/${userId}/sites?role=supportAccount`).pipe(
      map(response => (response?.sites ?? []).map((site: any) => ({
        ...site,
        siteId: site.siteId?.replace(/^@/, '') ?? ''
      })))
    );
  }

  getSiteById(siteId: string): Observable<any> {
    const data = `ver:"2.0"\nid\n${siteId}`;
    let region = this.configService.getSelectedRegion();
    return this.http.post(`${this.configService.getConfig(region).haystackUrl}v1/read`, data).pipe(catchError(this.handleError));
  }


  /**
 * Get the logged in user object
 * @returns Logged in user info
 */
 
  getUser() {
    const url = `${this.configService.getConfig('auth').caretakerUrl}/token/userinfo`;
    return this.http.get(url).pipe(catchError(this.handleError));
  }

  // returns the IANA timezone from a Haystack timezone
  // e.g. when converting tz from site entity
  getIANATimeZone(timezone: string) {
    if (timezone === 'Calcutta') {
      timezone = 'Kolkata';
    }
    return this.haystackTimezoneMap[timezone];
  }

  getReadById(pointId: string) {
    if(!pointId) {
      return of(null);
    }
    const data = `ver:"2.0"\nid\n@${pointId}`;
    let region = this.configService.getSelectedRegion();
    return this.http.post(`${this.configService.getConfig(region).haystackUrl}v1/read`, data).pipe(catchError(this.handleError));
  }

  private handleError(error: any): Promise<any> {
    return Promise.reject(error);
  }

   getmultiSiteTags(siteRefs: string[]): Observable<any> {
    let data = `ver:"3.0"\nid`;
    siteRefs.forEach((siteRef) => {
      data += `\n@${siteRef}`;
    });
    let region = this.configService.getSelectedRegion();
    return this.http.post(`${this.configService.getConfig(region).haystackUrl}v2/sitePointTags`, data).pipe(catchError(this.handleError));
  }

  stripHaystackTypeMapping(response: any) {
    return (response) ? JSON.parse(JSON.stringify(response).replace(/"r:|"c:|"n:|"t:|"b:|"m:|"z:|"s:|"d:/g, '"')) : '';
  }

  findByFilter(query:string) {
    const data = `ver:"3.0"\nfilter\n"${query}"`;
     let region = this.configService.getSelectedRegion();
    return this.http.post(`${this.configService.getConfig(region).haystackUrl}v1/read`, data).pipe(catchError(this.handleError));
  }

}
